import { Page, Text, BlockStack, Box, InlineStack, Icon } from '@shopify/polaris';
import { ChatIcon, ExternalIcon, BookIcon } from '@shopify/polaris-icons';
import { openCrispChat } from '../components/CrispChat';
import { t } from '../i18n';

const DOCS_URL = 'https://freebi-1.gitbook.io/freebi-docs';

/**
 * Help hub: live chat (Crisp) + documentation.
 */
export function HelpPage() {
  return (
    <Page title={t("Help")} subtitle={t("Get support or browse the Freebi docs")}>
      <BlockStack gap="500">
        {/* Chat card — matches support promo style */}
        <Box
          padding="500"
          background="bg-surface"
          borderRadius="300"
          borderWidth="025"
          borderColor="border"
          shadow="100"
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 24,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 280px', minWidth: 0 }}>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {t("Need some help to move faster?")}
                </Text>
                <Text as="p" tone="subdued">
                  {t("Get instant help from our human support team directly via\n                  in-app live chat, ready to guide you through setup or any\n                  questions along the way.")}
                </Text>
                <div>
                  <button
                    type="button"
                    onClick={() => openCrispChat()}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 18px',
                      borderRadius: 10,
                      border: '1px solid #C9CCCF',
                      background: '#fff',
                      color: '#202223',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        width: 20,
                        height: 20,
                        color: '#2C6ECB',
                      }}
                    >
                      <Icon source={ChatIcon} tone="info" />
                    </span>
                    {t("Chat with us")}
                  </button>
                </div>
              </BlockStack>
            </div>
            <div
              aria-hidden
              style={{
                flex: '0 0 auto',
                width: 140,
                height: 110,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SupportIllustration />
            </div>
          </div>
        </Box>

        {/* Docs card */}
        <Box
          padding="500"
          background="bg-surface"
          borderRadius="300"
          borderWidth="025"
          borderColor="border"
          shadow="100"
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 24,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 280px', minWidth: 0 }}>
              <BlockStack gap="300">
                <InlineStack gap="200" blockAlign="center">
                  <span style={{ display: 'inline-flex', color: '#008060' }}>
                    <Icon source={BookIcon} tone="success" />
                  </span>
                  <Text as="h2" variant="headingMd">
                    {t("Detailed help documentation")}
                  </Text>
                </InlineStack>
                <Text as="p" tone="subdued">
                  {t("Step-by-step guides for campaigns, widgets, BXGY, milestones,\n                  free gifts, and storefront setup.")}
                </Text>
                <div>
                  <a
                    href={DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 18px',
                      borderRadius: 10,
                      border: 'none',
                      background: '#008060',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 600,
                      textDecoration: 'none',
                      boxShadow: '0 4px 12px rgba(0, 128, 96, 0.25)',
                    }}
                  >
                    {t("Open Freebi docs")}
                    <span style={{ display: 'inline-flex', width: 18, height: 18 }}>
                      <Icon source={ExternalIcon} tone="inherit" />
                    </span>
                  </a>
                </div>
              </BlockStack>
            </div>
            <div
              aria-hidden
              style={{
                flex: '0 0 auto',
                width: 120,
                height: 100,
                borderRadius: 16,
                background:
                  'linear-gradient(145deg, #E3F1DF 0%, #F1F8F5 50%, #EAF4FF 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #AEE9D1',
              }}
            >
              <span style={{ fontSize: 40, lineHeight: 1 }}>📚</span>
            </div>
          </div>
        </Box>

      </BlockStack>
    </Page>
  );
}

function SupportIllustration() {
  return (
    <svg
      width="140"
      height="110"
      viewBox="0 0 140 110"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <ellipse cx="100" cy="95" rx="36" ry="8" fill="#F1F2F3" />
      <rect x="72" y="48" width="52" height="38" rx="6" fill="#FF6B8A" />
      <rect x="78" y="54" width="40" height="22" rx="3" fill="#1A1A2E" />
      <circle cx="98" cy="65" r="3" fill="#FFE66D" />
      <path
        d="M48 38c0-14 10-24 24-24s24 10 24 24v8H48v-8z"
        fill="#FFD6A5"
      />
      <circle cx="72" cy="28" r="16" fill="#FFD6A5" />
      <path
        d="M56 22c4-8 12-10 18-8"
        stroke="#2C2C2C"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M48 46h48v28c0 8-6 14-14 14H62c-8 0-14-6-14-14V46z"
        fill="#E8A0BF"
      />
      <rect x="44" y="50" width="10" height="18" rx="4" fill="#FFD6A5" />
      <rect x="90" y="50" width="10" height="18" rx="4" fill="#FFD6A5" />
      <rect x="18" y="30" width="28" height="22" rx="8" fill="#EEF0F3" />
      <circle cx="28" cy="41" r="5" fill="#8C9196" opacity="0.5" />
      <text x="40" y="46" fontSize="14" fill="#5C5F62">
        ?
      </text>
    </svg>
  );
}
